// components/QuickMenu.tsx
import React, { useState, useEffect, useLayoutEffect, useRef, useCallback, useMemo } from 'react';
import { MenuItemState, QuickMenuAction, QuickMenuData } from '#types/types';
import { useKernel } from '@/kernel/Kernel';

// useLayoutEffect warns during SSR; fall back to useEffect on the server.
const useIsomorphicLayoutEffect = typeof window !== 'undefined' ? useLayoutEffect : useEffect;
const VIEWPORT_MARGIN = 8;
const SUBMENU_HOVER_DELAY = 120;

interface QuickMenuProps {
  menuData: QuickMenuData | null;
}

export const QuickMenu: React.FC<QuickMenuProps> = ({ menuData }) => {
  const { kernel } = useKernel();
  const menuRef = useRef<HTMLDivElement>(null);
  const subMenuRef = useRef<HTMLDivElement>(null);
  const [hoveredItemIndex, setHoveredItemIndex] = useState<number | null>(null);
  const [hoveredSubItemIndex, setHoveredSubItemIndex] = useState<number | null>(null);
  const [activeSubMenu, setActiveSubMenu] = useState<{
    parentItemIndex: number;
    parentRect: DOMRect;
    actions: QuickMenuAction[];
  } | null>(null);
  const subMenuOpenTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const subMenuIntentTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearSubMenuOpenTimer = useCallback(() => {
    if (subMenuOpenTimerRef.current) {
      clearTimeout(subMenuOpenTimerRef.current);
      subMenuOpenTimerRef.current = null;
    }
  }, []);

  const clearSubMenuIntentTimer = useCallback(() => {
    if (subMenuIntentTimerRef.current) {
      clearTimeout(subMenuIntentTimerRef.current);
      subMenuIntentTimerRef.current = null;
    }
  }, []);

  const clearHoverTimers = useCallback(() => {
    clearSubMenuOpenTimer();
    clearSubMenuIntentTimer();
  }, [clearSubMenuIntentTimer, clearSubMenuOpenTimer]);

  const scheduleSubMenuDismissal = useCallback(() => {
    clearSubMenuIntentTimer();
    subMenuIntentTimerRef.current = setTimeout(() => {
      subMenuIntentTimerRef.current = null;
      setActiveSubMenu(null);
      setHoveredSubItemIndex(null);
    }, SUBMENU_HOVER_DELAY);
  }, [clearSubMenuIntentTimer]);

  // Viewport-clamped submenu position, computed after measuring the rendered flyout.
  // `forIndex` ties the position to its parent item so we never show a stale position
  // when switching between submenus (Agent <-> Mode).
  const [subMenuPos, setSubMenuPos] = useState<{ top: number; left: number; forIndex: number } | null>(null);

  const handleOutsideClick = useCallback((event: MouseEvent | TouchEvent) => {
    clearHoverTimers();
    const target = event.target as Node;
    const isOutsideMainMenu = menuRef.current && !menuRef.current.contains(target);
    const isOutsideSubMenu = activeSubMenu && subMenuRef.current && !subMenuRef.current.contains(target);

    if (isOutsideMainMenu && (isOutsideSubMenu || !activeSubMenu) ) {
      kernel.current?.setMenuData(null);
      setActiveSubMenu(null);
      setHoveredSubItemIndex(null);
    } else if (isOutsideMainMenu && activeSubMenu && !isOutsideSubMenu) {
      // Click was inside sub-menu but outside main menu - this is fine
    } else if (!isOutsideMainMenu && activeSubMenu && isOutsideSubMenu) {
      setActiveSubMenu(null);
      setHoveredSubItemIndex(null);
    }
  }, [activeSubMenu, clearHoverTimers, kernel]);

  const handleEscape = useCallback((event: KeyboardEvent) => {
    if (event.key !== 'Escape') return;
    clearHoverTimers();
    // A submenu is dismissed first, so Escape steps back out rather than closing everything.
    if (activeSubMenu) {
      setActiveSubMenu(null);
      setHoveredSubItemIndex(null);
      return;
    }
    kernel.current?.setMenuData(null);
  }, [activeSubMenu, clearHoverTimers, kernel]);

  useEffect(() => {
    clearHoverTimers();
    // eslint-disable-next-line react-hooks/set-state-in-effect -- resetting the open submenu when the menu data changes IS this effect's purpose. Pre-existing pattern, annotated rather than restructured.
    setActiveSubMenu(null);
    setHoveredItemIndex(null);
    setHoveredSubItemIndex(null);
  }, [clearHoverTimers, menuData]);

  // Measure the rendered submenu and clamp it inside the viewport: shift it up when
  // it would overflow the bottom (the chat menu lives near the screen bottom, so its
  // submenu used to grow off-screen), and flip it to the left of the parent when it
  // would overflow the right edge.
  useIsomorphicLayoutEffect(() => {
    if (!activeSubMenu) {
      setSubMenuPos(null);
      return;
    }
    const el = subMenuRef.current;
    if (!el) return;

    const rect = el.getBoundingClientRect();
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const { parentRect } = activeSubMenu;

    // Horizontal: prefer opening to the right; flip to the left if it would overflow.
    let left = parentRect.right + 2;
    if (left + rect.width > vw - VIEWPORT_MARGIN) {
      const flipped = parentRect.left - rect.width - 2;
      left = flipped >= VIEWPORT_MARGIN ? flipped : Math.max(VIEWPORT_MARGIN, vw - rect.width - VIEWPORT_MARGIN);
    }

    // Vertical: align to the parent's top; shift up if it would overflow the bottom.
    let top = parentRect.top;
    if (top + rect.height > vh - VIEWPORT_MARGIN) {
      top = vh - rect.height - VIEWPORT_MARGIN;
    }
    if (top < VIEWPORT_MARGIN) top = VIEWPORT_MARGIN;

    setSubMenuPos({ top, left, forIndex: activeSubMenu.parentItemIndex });
  }, [activeSubMenu]);

  useEffect(() => {
    let timeoutId: NodeJS.Timeout | null = null;
    if (menuData) {
       timeoutId = setTimeout(() => {
         document.addEventListener('click', handleOutsideClick);
         document.addEventListener('contextmenu', handleOutsideClick);
         document.addEventListener('touchstart', handleOutsideClick);
         window.addEventListener('keydown', handleEscape);
       }, 0);
    }

    return () => {
      if (timeoutId) clearTimeout(timeoutId);
      document.removeEventListener('click', handleOutsideClick);
      document.removeEventListener('contextmenu', handleOutsideClick);
      document.removeEventListener('touchstart', handleOutsideClick);
      window.removeEventListener('keydown', handleEscape);
      clearHoverTimers();
    };
  }, [menuData, handleOutsideClick, handleEscape, clearHoverTimers]);

  const menuStyles = useMemo((): { anchor: React.CSSProperties; panel: React.CSSProperties; origin: string } | undefined => {
    if (!menuData) return undefined;

    const { x: clickX, y: clickY } = menuData.position;
    let finalX = clickX;
    let finalY = clickY;
    let transformX = '';
    let transformY = '';
    let transformOriginX = 'left';
    let transformOriginY = 'top';

    if (typeof window !== 'undefined') {
        const windowWidth = window.innerWidth;
        const windowHeight = window.innerHeight;

        if (clickX > windowWidth / 2) {
            transformX = 'translateX(-100%)';
            transformOriginX = 'right';
        }

        if (clickY > windowHeight / 2) {
            transformY = 'translateY(-100%)';
            transformOriginY = 'bottom';
        }
    }

    const transform = `${transformX} ${transformY}`.trim();
    const opensFromRight = transformOriginX === 'right';
    const opensFromBottom = transformOriginY === 'bottom';
    const clipStart = opensFromBottom
      ? (opensFromRight ? 'inset(100% 0 0 100%)' : 'inset(100% 100% 0 0)')
      : (opensFromRight ? 'inset(0 0 100% 100%)' : 'inset(0 100% 100% 0)');
    const quickMenuMotionVars = {
      '--quick-menu-clip-start': clipStart,
      '--quick-menu-item-x': opensFromRight ? '4px' : '-4px',
      '--quick-menu-item-y': opensFromBottom ? '3px' : '-3px',
      '--quick-menu-glow-x': opensFromRight ? '-18px' : '18px',
      '--quick-menu-glow-y': opensFromBottom ? '-18px' : '18px',
    } as Record<string, string>;
    const origin = `${transformOriginY}-${transformOriginX}`;
    // ChatGPT's + menu (owner, 2026-09-28): the chat box's width, a solid #353535 panel with 20px
    // corners, no glass. Only the composer "+" asks for it; every context menu keeps its own skin.
    const composer = menuData.skin === 'composer';

    return {
      anchor: {
        position: 'fixed',
        top: finalY,
        left: finalX,
        transform: transform,
        zIndex: 1000,
        whiteSpace: 'nowrap',
        ...(composer && menuData.width ? { width: menuData.width } : {}),
      },
      panel: composer ? {
        position: 'relative',
        overflow: 'hidden',
        background: '#353535',
        color: 'white',
        border: '1px solid rgba(255, 255, 255, 0.06)',
        borderRadius: '20px',
        padding: '6px',
        boxShadow: '0 18px 52px rgba(0, 0, 0, 0.5)',
        transformOrigin: `${transformOriginY} ${transformOriginX}`,
        ...quickMenuMotionVars,
      } : {
        position: 'relative',
        overflow: 'hidden',
        background: '#353535',
        color: '#ECECEC',
        border: '1px solid rgba(255, 255, 255, 0.06)',
        borderRadius: '16px',
        padding: '6px',
        minWidth: 200,
        boxShadow: '0 18px 52px rgba(0, 0, 0, 0.5)',
        transformOrigin: `${transformOriginY} ${transformOriginX}`,
        ...quickMenuMotionVars,
      },
      origin,
    };
  }, [menuData]);

  const handleMenuDivClick = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
  }, []);

  const handleMenuDivContextMenu = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleActionItemClick = useCallback((action: QuickMenuAction, event: any) => {
    if (action.disabled) return;
    const actionSpecificOnClick = action.onClick;
    clearHoverTimers();
    actionSpecificOnClick(event);
    kernel.current?.setMenuData(null);
    setActiveSubMenu(null);
    setHoveredSubItemIndex(null);
  }, [clearHoverTimers, kernel]);

  const handleActionItemContextMenu = useCallback((action: QuickMenuAction, event: any) => {
    if (action.disabled || !action.onContextMenu) return;
    event.preventDefault();
    event.stopPropagation();
    clearHoverTimers();
    action.onContextMenu(event);
    kernel.current?.setMenuData(null);
    setActiveSubMenu(null);
    setHoveredSubItemIndex(null);
  }, [clearHoverTimers, kernel]);

  if (!menuData || !menuStyles) {
    return null;
  }

  // Only show the submenu once its viewport-clamped position is computed for THIS
  // parent, avoids a one-frame flash at a stale position when switching submenus.
  const subMenuReady = !!activeSubMenu && subMenuPos?.forIndex === activeSubMenu.parentItemIndex;

  return (
    <>
      <div
        ref={menuRef}
        id="quick-menu-root"
        style={menuStyles.anchor}
        onClick={handleMenuDivClick}
        onContextMenu={handleMenuDivContextMenu}
        onMouseEnter={clearSubMenuIntentTimer}
        onMouseLeave={scheduleSubMenuDismissal}
      >
        <div
          key={`${menuData.position.x}:${menuData.position.y}:${menuData.actions.length}`}
          // The chat + menu just fades in (owner, 2026-09-28): the corner reveal squared off and lit
          // the corner it grew from, which read as a notch on a round panel.
          className={menuData.skin === 'composer' ? 'animate-in fade-in-0 duration-150' : 'quick-menu-panel'}
          data-origin={menuStyles.origin}
          style={menuStyles.panel}
        >
        {menuData.actions.map((action: QuickMenuAction, idx: number) => {
          const isDirectlyHovered = hoveredItemIndex === idx;
          const isParentOfOpenSubMenu = activeSubMenu?.parentItemIndex === idx;
          
          const menuItemState: MenuItemState = {
            isSelected: !!action.selected,
            isHovered: isDirectlyHovered || isParentOfOpenSubMenu,
          };

          const iconContent = typeof action.icon === 'function'
            ? action.icon(menuItemState)
            : action.icon;

          const mainContent = typeof action.customContent === 'function'
            ? action.customContent(menuItemState)
            : (action.customContent !== undefined ? action.customContent : action.label);
          
          const hasSubActions = !action.disabled && action.subActions && action.subActions.length > 0;
          const actionTooltip = typeof action.tooltip === 'string' && action.tooltip.trim().length > 0
            ? action.tooltip
            : undefined;

          return (
            <React.Fragment key={`main-fragment-${idx}`}>
              {action.separatorBefore && idx > 0 && (
                <div
                  style={{
                    height: '1px',
                    margin: '6px 10px',
                    background: 'rgba(255, 255, 255, 0.1)',
                  }}
                />
              )}
              <button
                key={`main-${idx}`}
                onClick={(e) => {
                  e.stopPropagation();
                  if (action.disabled) return;
                  if (!hasSubActions) {
                    handleActionItemClick(action, e);
                  } else {
                    clearHoverTimers();
                    action.onClick(e);

                    const currentTargetRect = e.currentTarget.getBoundingClientRect();
                    if (activeSubMenu && activeSubMenu.parentItemIndex === idx) {
                      setActiveSubMenu(null);
                      setHoveredSubItemIndex(null);
                    } else {
                      setActiveSubMenu({
                        parentItemIndex: idx,
                        parentRect: currentTargetRect,
                        actions: action.subActions!,
                      });
                    }
                  }
                }}
                onContextMenu={(e) => {
                  if (!action.onContextMenu) return;
                  handleActionItemContextMenu(action, e);
                }}
                onMouseEnter={(e) => {
                  setHoveredItemIndex(idx);
                  clearSubMenuIntentTimer();
                  clearSubMenuOpenTimer();
                  if (hasSubActions) {
                    const currentTargetRect = e.currentTarget.getBoundingClientRect();
                    subMenuOpenTimerRef.current = setTimeout(() => {
                      subMenuOpenTimerRef.current = null;
                      setActiveSubMenu({
                        parentItemIndex: idx,
                        parentRect: currentTargetRect,
                        actions: action.subActions!,
                      });
                    }, SUBMENU_HOVER_DELAY);
                  } else if (activeSubMenu && activeSubMenu.parentItemIndex !== idx) {
                    scheduleSubMenuDismissal();
                  }
                }}
                onMouseLeave={() => {
                  setHoveredItemIndex(null);
                  clearSubMenuOpenTimer();
                }}
                className={`classic ${menuData.skin === 'composer' ? '' : 'quick-menu-item-open'} ${action.selected ? 'blue--selected' : ''} focus:outline-none focus:ring-2 focus:ring-white/20`}
                disabled={action.disabled === true}
                style={{
                  justifyContent: 'space-between',
                  display: 'flex',
                  alignItems: 'center',
                  color: action.disabled ? 'rgba(255, 255, 255, 0.35)' : action.danger ? '#F28B82' : (action.selected ? '#ECECEC' : 'inherit'),
                  padding: menuData.skin === 'composer' ? '8px 10px' : '10px 12px',
                  textAlign: 'left',
                  width: '100%',
                  border: 'none',
                  borderRadius: menuData.skin === 'composer' ? '12px' : '10px',
                  cursor: action.disabled ? 'not-allowed' : 'pointer',
                  fontSize: '14px',
                  backgroundColor: isParentOfOpenSubMenu ? 'rgba(255, 255, 255, 0.1)' : undefined,
                  opacity: action.disabled ? 0.68 : 1,
                  animationDelay: `${Math.min(idx * 24, 168)}ms`,
                }}
                aria-label={action.label}
                aria-haspopup={hasSubActions ? "menu" : undefined}
                aria-expanded={activeSubMenu?.parentItemIndex === idx ? "true" : "false"}
                title={actionTooltip}
              >
                <div style={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
                  {iconContent && <span style={{ marginRight: 12, display: 'flex', alignItems: 'center' }}>{iconContent}</span>}
                  <span style={{ display: 'flex', alignItems: 'center', fontSize: 14, ...(menuData.skin === 'composer' ? { fontWeight: 500 } : {}) }}>{mainContent}</span>
                  {menuData.skin === 'composer' && action.hint ? (
                    <span style={{ marginLeft: 10, fontSize: 14, color: '#AFAFAF', overflow: 'hidden', textOverflow: 'ellipsis' }}>{action.hint}</span>
                  ) : null}
                </div>
                {hasSubActions && <span style={{ marginLeft: '12px', fontSize: '0.8em' }}>{'\u25B6'}</span>}
              </button>
            </React.Fragment>
          );
        })}
        </div>
      </div>

      {activeSubMenu && (
        <div
          ref={subMenuRef}
          style={{
            position: 'fixed',
            top: subMenuReady ? subMenuPos!.top : activeSubMenu.parentRect.top,
            left: subMenuReady ? subMenuPos!.left : activeSubMenu.parentRect.right + 2,
            visibility: subMenuReady ? 'visible' : 'hidden',
            maxHeight: `calc(100vh - ${VIEWPORT_MARGIN * 2}px)`,
            overflowY: 'auto',
            zIndex: 1001,
            background: '#353535',
            color: 'white',
            borderRadius: '6px',
            padding: '8px',
            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
            whiteSpace: 'nowrap',
          }}
          onMouseEnter={clearHoverTimers}
          onMouseLeave={scheduleSubMenuDismissal}
          onClick={(e) => e.stopPropagation()}
          onContextMenu={(e) => { e.preventDefault(); e.stopPropagation();}}
        >
          {activeSubMenu.actions.map((subAction, subIdx) => {
            const subMenuItemState: MenuItemState = { 
                isSelected: !!subAction.selected, 
                isHovered: hoveredSubItemIndex === subIdx
            };

            const subIconContent = typeof subAction.icon === 'function'
              ? subAction.icon(subMenuItemState)
              : subAction.icon;
            
            const subMainContent = typeof subAction.customContent === 'function'
              ? subAction.customContent(subMenuItemState)
              : (subAction.customContent !== undefined ? subAction.customContent : subAction.label);
            const subActionTooltip = typeof subAction.tooltip === 'string' && subAction.tooltip.trim().length > 0
              ? subAction.tooltip
              : undefined;

            return (
              <React.Fragment key={`sub-fragment-${activeSubMenu.parentItemIndex}-${subIdx}`}>
                {subAction.separatorBefore && subIdx > 0 && (
                  <div
                    style={{
                      height: '1px',
                      margin: '6px 6px',
                      background: 'rgba(255, 255, 255, 0.14)',
                    }}
                  />
                )}
                <button
                  key={`sub-${activeSubMenu.parentItemIndex}-${subIdx}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleActionItemClick(subAction, e);
                  }}
                  onContextMenu={(e) => {
                    if (!subAction.onContextMenu) return;
                    handleActionItemContextMenu(subAction, e);
                  }}
                  onMouseEnter={() => setHoveredSubItemIndex(subIdx)}
                  onMouseLeave={() => setHoveredSubItemIndex(null)}
                  className={`classic ${subAction.selected ? 'blue--selected' : ''} focus:outline-none focus:ring-2 focus:ring-white/20`}
                  disabled={subAction.disabled === true}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    color: subAction.disabled ? 'rgba(255, 255, 255, 0.35)' : (subAction.selected ? '#ECECEC' : 'inherit'),
                    padding: '8px 12px',
                    textAlign: 'left',
                    width: '100%',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: subAction.disabled ? 'not-allowed' : 'pointer',
                    fontSize: '14px',
                    opacity: subAction.disabled ? 0.68 : 1,
                  }}
                  aria-label={subAction.label}
                  title={subActionTooltip}
                >
                  <div style={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
                    {subIconContent && <span style={{ marginRight: '8px', display: 'flex', alignItems: 'center' }}>{subIconContent}</span>}
                    <span style={{ display: 'flex', alignItems: 'center' }}>{subMainContent}</span>
                  </div>
                </button>
              </React.Fragment>
            );
          })}
        </div>
      )}
    </>
  );
};
